import React, { useState, useEffect } from 'react';
import { fetchArticles } from '../../hooks/useMarketIntelligence';

function timeAgo(dateStr) {
  const seconds = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

const SENTIMENT_CHIP = {
  bullish: { bg: 'bg-emerald-500/20', border: 'border-emerald-500/30', text: 'text-emerald-400', label: 'bullish' },
  bearish: { bg: 'bg-rose-500/20', border: 'border-rose-500/30', text: 'text-rose-400', label: 'bearish' },
  neutral: { bg: 'bg-slate-500/20', border: 'border-slate-500/30', text: 'text-slate-400', label: 'neutral' },
};

export default function LatestHeadlinesCard({ config }) {
  const apiBaseUrl = config?.apiBaseUrl || window.location.origin.replace('/web', '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchArticles(apiBaseUrl, { min_impact: 0.5, limit: 10, time_window: '24h' })
      .then(setData)
      .catch(setError)
      .finally(() => setLoading(false));
  }, [apiBaseUrl]);

  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
      <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
        <svg className="w-4 h-4 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
        </svg>
        Latest Headlines
        <span className="ml-auto text-xs text-slate-500 font-normal">24h · impact ≥ 0.5</span>
      </h4>

      {loading && (
        <div className="flex items-center justify-center py-6">
          <svg className="animate-spin h-5 w-5 text-cyan-400" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        </div>
      )}

      {error && (
        <p className="text-xs text-slate-500 py-4 text-center">Headlines unavailable</p>
      )}

      {data && (
        <div className="divide-y divide-slate-700/40">
          {[...(data.articles || [])].sort((a, b) => new Date(b.published_at) - new Date(a.published_at)).map((article) => (
            <a
              key={article.id}
              href={article.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col sm:flex-row sm:items-center gap-x-2 gap-y-1 py-1.5 hover:bg-slate-700/20 transition-colors min-w-0"
            >
              {/* Row 1 (mobile) / all inline (sm+): sentiment chip + title */}
              <div className="flex items-center gap-2 min-w-0">
                <span className={`shrink-0 px-2 py-0.5 border text-xs rounded ${SENTIMENT_CHIP[article.sentiment?.direction]?.bg || SENTIMENT_CHIP.neutral.bg} ${SENTIMENT_CHIP[article.sentiment?.direction]?.border || SENTIMENT_CHIP.neutral.border} ${SENTIMENT_CHIP[article.sentiment?.direction]?.text || SENTIMENT_CHIP.neutral.text}`}>
                  {SENTIMENT_CHIP[article.sentiment?.direction]?.label || SENTIMENT_CHIP.neutral.label}
                </span>
                <span className="flex-1 min-w-0 text-xs text-slate-200 truncate hover:underline">
                  {article.title}
                </span>
              </div>
              {/* Row 2 (mobile) / right side (sm+): tokens + time */}
              <div className="flex items-center gap-1 sm:ml-auto shrink-0 pl-0 sm:pl-0">
                {article.token_symbols?.length > 0 && (
                  <>
                    {article.token_symbols.slice(0, 3).map((symbol) => (
                      <span key={symbol} className="px-1.5 py-0.5 bg-slate-700/50 border border-slate-600 text-slate-300 text-xs rounded">
                        {symbol}
                      </span>
                    ))}
                    {article.token_symbols.length > 3 && (
                      <span className="px-1.5 py-0.5 bg-slate-700/50 border border-slate-600 text-slate-400 text-xs rounded">
                        +{article.token_symbols.length - 3}
                      </span>
                    )}
                  </>
                )}
                <span className="text-xs text-slate-400 ml-1">{timeAgo(article.published_at)}</span>
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
